import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarDays, ArrowUpRight, Clock } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Group events by their UTC date (matches how the Calendar page treats Graph datetimes).
function dayLabel(dateStr) {
  const today = ymd(new Date());
  const t = new Date(); t.setDate(t.getDate() + 1);
  const tomorrow = ymd(t);
  if (dateStr === today) return "Today";
  if (dateStr === tomorrow) return "Tomorrow";
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function DashboardCalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const now = new Date();
    const end = new Date(now); end.setDate(now.getDate() + 14);
    const start = `${ymd(now)}T00:00:00`;
    const endIso = `${ymd(end)}T23:59:59`;
    api.get("/calendar/events", { params: { start, end: endIso, mailbox: "me" } })
      .then((r) => setEvents((r.data.events || []).slice(0, 6)))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const grouped = events.reduce((acc, ev) => {
    const k = (ev.start?.dateTime || "").slice(0, 10);
    (acc[k] = acc[k] || []).push(ev);
    return acc;
  }, {});

  return (
    <div className="bg-white border border-gray-200 rounded-sm p-6 shadow-sm" data-testid="dashboard-calendar">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-[#002FA7]" /> My calendar
        </h3>
        <button onClick={() => navigate("/calendar")} data-testid="dashboard-calendar-open"
          className="flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline">
          Open <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="space-y-2" data-testid="dashboard-calendar-loading">
          {[0, 1, 2].map((i) => <div key={i} className="h-9 bg-gray-100 rounded-sm animate-pulse" />)}
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-8" data-testid="dashboard-calendar-empty">
          <p className="text-sm text-gray-400">No upcoming events in the next 14 days.</p>
          <button onClick={() => navigate("/calendar")} className="mt-3 text-xs font-semibold text-[#002FA7] hover:underline">Add an event</button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="dashboard-calendar-list">
          {Object.keys(grouped).sort().map((dateStr) => (
            <div key={dateStr}>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5">{dayLabel(dateStr)}</p>
              <div className="space-y-1.5">
                {grouped[dateStr].map((ev) => (
                  <button key={ev.id} onClick={() => navigate("/calendar")} data-testid={`dashboard-event-${ev.id}`}
                    className="w-full flex items-center gap-2.5 text-left px-2 py-1.5 rounded-sm hover:bg-gray-50 transition-colors">
                    <span className="w-1 h-7 rounded-full bg-[#002FA7] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{ev.subject || "(No title)"}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {ev.isAllDay ? "All day" : `${(ev.start?.dateTime || "").slice(11, 16)}–${(ev.end?.dateTime || "").slice(11, 16)} UTC`}
                        {ev.location?.displayName ? ` · ${ev.location.displayName}` : ""}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
